import { MetricDefinitionPart } from "@redotech/redo-model/checkouts/metric-definition";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { Flex } from "../flex";
import { ExternalLink } from "../link";
import { Text } from "../text";
import * as css from "./metric-tooltip.module.css";
import { Tooltip } from "./tooltip";

type MetricDefinition = { lhs: string; rhs: readonly MetricDefinitionPart[] };

export type RedoMetricTooltipProps = {
  title?: string | undefined;
  metricDescription?: string | string[] | undefined;
  metricDefinition?: MetricDefinition | undefined;
  metricEvaluation?: MetricDefinition | undefined;
  externalLink?:
    | { text: string; url: string; isInternal?: boolean }
    | undefined;
};

export const RedoMetricTooltip = memo(function RedoMetricTooltip({
  title,
  metricDescription,
  metricDefinition,
  metricEvaluation,
  externalLink,
  children,
}: { children: React.ReactElement<any, any> } & RedoMetricTooltipProps) {
  const titleComponent = useMemo(() => {
    if (title) {
      return (
        <Flex pb="xs">
          <Text fontSize="xs" fontWeight="semibold" textColor="white">
            {title}
          </Text>
        </Flex>
      );
    }
    return null;
  }, [title]);

  const metricDescriptionComponent = useMemo(() => {
    if (metricDescription) {
      if (Array.isArray(metricDescription)) {
        return metricDescription.map((line, index) => (
          <Text
            fontSize="xs"
            fontWeight="medium"
            key={index}
            textColor="tooltip-supporting-text"
          >
            {line}
          </Text>
        ));
      } else if (typeof metricDescription === "string") {
        return (
          <Text
            fontSize="xs"
            fontWeight="medium"
            textColor="tooltip-supporting-text"
          >
            {metricDescription}
          </Text>
        );
      } else {
        assertNever(metricDescription);
      }
    }
    return null;
  }, [metricDescription]);

  const renderMetricFormula = useMemo(() => {
    const MetricFormula = (formula: MetricDefinition) => (
      <Flex as="span" display="inline" pt="2xl">
        <Text
          as="span"
          fontFamily="monospace"
          fontSize="xs"
          fontWeight="medium"
          textColor="tooltip-supporting-text"
        >
          {formula.lhs}
        </Text>{" "}
        ={" "}
        {formula.rhs.map((part, index) => {
          switch (part.type) {
            case "operator":
              return (
                <Text
                  as="span"
                  fontFamily="monospace"
                  fontSize="xs"
                  fontWeight="medium"
                  key={index}
                  textColor="tooltip-supporting-text"
                >{` ${part.operator} `}</Text>
              );
            case "left-parenthesis":
              return (
                <Text
                  as="span"
                  fontFamily="monospace"
                  fontSize="xs"
                  fontWeight="medium"
                  key={index}
                  textColor="tooltip-supporting-text"
                >
                  {"( "}
                </Text>
              );
            case "right-parenthesis":
              return (
                <Text
                  as="span"
                  fontFamily="monospace"
                  fontSize="xs"
                  fontWeight="medium"
                  key={index}
                  textColor="tooltip-supporting-text"
                >
                  {" )"}
                </Text>
              );
            case "term":
              return (
                <Text
                  as="span"
                  fontFamily="monospace"
                  fontSize="xs"
                  fontWeight="medium"
                  key={index}
                  textColor="tooltip-calculation-text"
                >
                  {part.value}
                </Text>
              );
            default:
              assertNever(part);
          }
        })}
      </Flex>
    );
    return MetricFormula;
  }, []);

  const metricDefinitionComponent = useMemo(() => {
    return metricDefinition ? renderMetricFormula(metricDefinition) : null;
  }, [metricDefinition, renderMetricFormula]);

  const metricEvaluationComponent = useMemo(() => {
    return metricEvaluation ? renderMetricFormula(metricEvaluation) : null;
  }, [metricEvaluation, renderMetricFormula]);

  const externalLinkComponent = useMemo(() => {
    if (externalLink) {
      return (
        <Flex pt="2xl">
          {externalLink.isInternal ? (
            <Link className={css.externalLink} to={externalLink.url}>
              <span className={css.internalLinkLabel}>{externalLink.text}</span>
            </Link>
          ) : (
            <ExternalLink
              className={css.externalLink}
              showIcon={false}
              url={externalLink.url}
            >
              {externalLink.text}
            </ExternalLink>
          )}
        </Flex>
      );
    }
    return null;
  }, [externalLink]);

  const content = useMemo(() => {
    return (
      <Flex dir="column">
        {titleComponent}
        {metricDescriptionComponent}
        {metricDefinitionComponent}
        {metricEvaluationComponent}
        {externalLinkComponent}
      </Flex>
    );
  }, [
    titleComponent,
    metricDescriptionComponent,
    metricDefinitionComponent,
    metricEvaluationComponent,
    externalLinkComponent,
  ]);

  return (
    <Tooltip arrow leftAlign paddingOverride="12px 12px" title={content}>
      {children}
    </Tooltip>
  );
});
